import React from 'react';
import { motion } from 'framer-motion';
interface CircleVisualProps {
  radius?: number;
  diameter?: number;
  circumference?: number;
  area?: number;
  label?: string;
  showFormula?: boolean;
  highlightRadius?: boolean;
  highlightDiameter?: boolean;
  sectorAngle?: number;
  highlightSector?: boolean;
  mode?: 'stimulus' | 'support';
}
/**
 * Helper: build an SVG arc path for a sector from 0° to `angleDeg`.
 * The sector starts at 12-o'clock (−90°) and sweeps clockwise.
 */
function sectorPath(cx: number, cy: number, r: number, angleDeg: number): string {
  if (angleDeg >= 360) {
    // Full circle — use two arcs
    return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r} Z`;
  }
  const startRad = -Math.PI / 2; // 12 o'clock
  const endRad = startRad + angleDeg * Math.PI / 180;
  const largeArc = angleDeg > 180 ? 1 : 0;
  const x1 = cx + r * Math.cos(startRad);
  const y1 = cy + r * Math.sin(startRad);
  const x2 = cx + r * Math.cos(endRad);
  const y2 = cy + r * Math.sin(endRad);
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}
export function CircleVisual({
  radius: propRadius,
  diameter: propDiameter,
  circumference,
  area,
  label,
  showFormula = false,
  highlightRadius = false,
  highlightDiameter = false,
  sectorAngle,
  highlightSector = false,
  mode = 'support'
}: CircleVisualProps) {
  // Calculate missing values
  const radius = propRadius ?? (propDiameter ? propDiameter / 2 : 50);
  const diameter = propDiameter ?? radius * 2;
  // SVG dimensions
  const svgSize = 280;
  const cx = svgSize / 2;
  const cy = svgSize / 2;
  // Scale circle to fill SVG nicely — ensure minimum visible size
  const maxDisplayRadius = svgSize * 0.38; // ~106px
  const minDisplayRadius = 40;
  const maxExpectedRadius = 20;
  const scaleFactor = maxDisplayRadius / maxExpectedRadius;
  const circleRadius = Math.max(minDisplayRadius, Math.min(maxDisplayRadius, radius * scaleFactor));
  const hasSector = typeof sectorAngle === 'number' && sectorAngle > 0 && sectorAngle < 360;
  return <div className="flex flex-col items-center gap-4 py-3" data-id="element-11817">
      {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-11818">
          {label}
        </p>}

      {/* Circle SVG */}
      <motion.svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`} initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.4
    }} className="drop-shadow-md" role="img" aria-label={hasSector ? `Cirkel met sector van ${sectorAngle}°` : 'Cirkel'} data-id="element-11819">
        {/* Full circle background */}
        <motion.circle cx={cx} cy={cy} r={circleRadius} fill={hasSector ? 'rgb(241, 245, 249)' : 'rgb(191, 219, 254)'} stroke="rgb(59, 130, 246)" strokeWidth="3" initial={{
        pathLength: 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 1,
        ease: 'easeInOut'
      }} data-id="element-11820" />

        {/* Sector wedge (when sectorAngle is provided) */}
        {hasSector && <motion.path d={sectorPath(cx, cy, circleRadius, sectorAngle)} fill="rgb(147, 197, 253)" stroke="rgb(59, 130, 246)" strokeWidth="2.5" strokeLinejoin="round" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.5,
        ease: 'easeOut'
      }} style={{
        transformOrigin: `${cx}px ${cy}px`
      }} data-id="element-11821" />}

        {/* Sector angle arc indicator */}
        {hasSector && sectorAngle <= 180 && (() => {
        const arcR = circleRadius * 0.25;
        const startRad = -Math.PI / 2;
        const endRad = startRad + sectorAngle * Math.PI / 180;
        const largeArc = sectorAngle > 180 ? 1 : 0;
        const ax1 = cx + arcR * Math.cos(startRad);
        const ay1 = cy + arcR * Math.sin(startRad);
        const ax2 = cx + arcR * Math.cos(endRad);
        const ay2 = cy + arcR * Math.sin(endRad);
        return <path d={`M ${ax1} ${ay1} A ${arcR} ${arcR} 0 ${largeArc} 1 ${ax2} ${ay2}`} fill="none" stroke="rgb(99, 102, 241)" strokeWidth="2" strokeDasharray="4,3" data-id="element-11822" />;
      })()}

        {/* Sector angle label */}
        {hasSector && (() => {
        const labelR = circleRadius * 0.35;
        const midAngleRad = -Math.PI / 2 + sectorAngle * Math.PI / 360;
        const lx = cx + labelR * Math.cos(midAngleRad);
        const ly = cy + labelR * Math.sin(midAngleRad);
        return <text x={lx} y={ly} textAnchor="middle" dominantBaseline="central" className="text-sm font-bold fill-indigo-700" data-id="element-11823">
                {sectorAngle}°
              </text>;
      })()}

        {/* Center point */}
        <circle cx={cx} cy={cy} r="4" fill="rgb(59, 130, 246)" data-id="element-11824" />

        {/* Radius line (if highlighted) */}
        {highlightRadius && <>
            <motion.line x1={cx} y1={cy} x2={cx + circleRadius} y2={cy} stroke="rgb(239, 68, 68)" strokeWidth="3" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} data-id="element-11825" />
            <text x={cx + circleRadius / 2} y={cy - 10} textAnchor="middle" className="text-sm font-bold fill-red-600" data-id="element-11826">
              r = {radius}
            </text>
          </>}

        {/* Diameter line (if highlighted) */}
        {highlightDiameter && <>
            <motion.line x1={cx - circleRadius} y1={cy} x2={cx + circleRadius} y2={cy} stroke="rgb(34, 197, 94)" strokeWidth="3" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} data-id="element-11827" />
            <text x={cx} y={cy + 25} textAnchor="middle" className="text-sm font-bold fill-green-600" data-id="element-11828">
              d = {diameter}
            </text>
          </>}
      </motion.svg>

      {/* Formula display */}
      {mode === 'support' && showFormula && <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 space-y-4 max-w-md w-full" data-id="element-11829">
          {circumference !== undefined && <div className="flex flex-col gap-1" data-id="element-11830">
              <span className="text-sm font-semibold text-blue-900 border-b border-blue-200 pb-1 mb-1" data-id="element-11831">
                Omtrek (C)
              </span>
              <div className="flex justify-between items-center" data-id="element-11832">
                <span className="text-sm text-blue-800" data-id="element-11833">Formule:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-11834">
                  C = 2πr
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-11835">
                <span className="text-sm text-blue-800" data-id="element-11836">Invullen:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-11837">
                  2 × π × {radius}
                </span>
              </div>
              <div className="flex justify-between items-center bg-blue-100 p-2 rounded-md mt-1" data-id="element-11838">
                <span className="text-sm font-bold text-blue-900" data-id="element-11839">
                  Uitkomst:
                </span>
                <span className="font-mono font-black text-blue-800" data-id="element-11840">
                  ≈ {circumference.toFixed(1)} cm
                </span>
              </div>
            </div>}
          {area !== undefined && <div className="flex flex-col gap-1" data-id="element-11841">
              <span className="text-sm font-semibold text-blue-900 border-b border-blue-200 pb-1 mb-1" data-id="element-11842">
                Oppervlakte (A)
              </span>
              <div className="flex justify-between items-center" data-id="element-11843">
                <span className="text-sm text-blue-800" data-id="element-11844">Formule:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-11845">
                  A = πr²
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-11846">
                <span className="text-sm text-blue-800" data-id="element-11847">Invullen:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-11848">
                  π × {radius}²
                </span>
              </div>
              <div className="flex justify-between items-center bg-blue-100 p-2 rounded-md mt-1" data-id="element-11849">
                <span className="text-sm font-bold text-blue-900" data-id="element-11850">
                  Uitkomst:
                </span>
                <span className="font-mono font-black text-blue-800" data-id="element-11851">
                  ≈ {area.toFixed(1)} cm²
                </span>
              </div>
            </div>}
          {!circumference && !area && <div className="space-y-1" data-id="element-11852">
              <p className="text-sm text-blue-900" data-id="element-11853">
                <span className="font-bold" data-id="element-11854">Omtrek:</span> C = 2πr of C = πd
              </p>
              <p className="text-sm text-blue-900" data-id="element-11855">
                <span className="font-bold" data-id="element-11856">Oppervlakte:</span> A = πr²
              </p>
            </div>}
        </div>}

      {/* Sector info card (when sector is shown) */}
      {hasSector && mode === 'support' && <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-5 py-3 shadow-sm" data-id="element-11857">
          <div className="flex flex-col gap-1" data-id="element-11858">
            <div className="flex items-center gap-2" data-id="element-11859">
              <span className="text-sm font-semibold text-indigo-900" data-id="element-11860">
                Sector:
              </span>
              <span className="text-lg font-black text-indigo-700" data-id="element-11861">
                {sectorAngle}°
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-11862">
              <span className="text-sm font-semibold text-indigo-900" data-id="element-11863">
                Deel:
              </span>
              <span className="text-lg font-black text-indigo-700" data-id="element-11864">
                {sectorAngle}/360
              </span>
            </div>
          </div>
        </div>}

      {/* Measurements card */}
      {mode === 'support' && (propRadius || propDiameter) && !hasSector && <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-5 py-3 shadow-sm" data-id="element-11865">
          <div className="flex flex-col gap-1" data-id="element-11866">
            {propRadius && <div className="flex items-center gap-2" data-id="element-11867">
                <span className="text-sm font-semibold text-indigo-900" data-id="element-11868">
                  Straal (r):
                </span>
                <span className="text-lg font-black text-indigo-700" data-id="element-11869">
                  {radius} cm
                </span>
              </div>}
            {propDiameter && <div className="flex items-center gap-2" data-id="element-11870">
                <span className="text-sm font-semibold text-indigo-900" data-id="element-11871">
                  Diameter (d):
                </span>
                <span className="text-lg font-black text-indigo-700" data-id="element-11872">
                  {diameter} cm
                </span>
              </div>}
          </div>
        </div>}
    </div>;
}